import React, { useEffect, useRef, useState } from 'react';
import type { TreeExpandedKeysType } from 'primereact/tree'; // <-- the type
import { Tree } from 'primereact/tree';
import { FileUpload } from 'primereact/fileupload';
import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import { InputText } from 'primereact/inputtext';
import { Toast } from 'primereact/toast';
import axios from 'axios';
import { downloadDocument } from 'app/api/downloadDocumentApi';
import { useAppSelector } from 'app/config/store';
import { hasAnyAuthority } from 'app/shared/auth/private-route';
import { AUTHORITIES } from 'app/config/constants';

export const DownloadDocument = () => {
  const [treeData, setTreeData] = useState([]);
  const [selectedNodeKeys, setSelectedNodeKeys] = useState(null);
  const [uploadDialogVisible, setUploadDialogVisible] = useState(false);
  const [newFolderDialogVisible, setNewFolderDialogVisible] = useState(false);
  const [newFolderName, setNewFolderName] = useState('');
  const [currentPath, setCurrentPath] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [latestDocuments, setLatestDocuments] = useState([]);
  const [deleteDocumentDialog, setDeleteDocumentDialog] = useState(false);
  const [seletedDeleteDocument, setSeletedDeleteDocument] = useState(null);
  const [deleteFolderDialog, setDeleteFolderDialog] = useState(false);
  const [seletedDeleteFolder, setSeletedDeleteFolder] = useState(null);
  const fileUploadRef = useRef(null);
  const toast = useRef(null);
  const [expandedKeys, setExpandedKeys] = useState<TreeExpandedKeysType>({});

  const account = useAppSelector(state => state.authentication.account);
  const isAdminOrAssistant = hasAnyAuthority(account.authorities, [AUTHORITIES.ADMIN, AUTHORITIES.SUPER_ADMIN, AUTHORITIES.ASSISTANT]);

  useEffect(() => {
    fetchFolderStructure();
  }, []);

  const fetchFolderStructure = () => {
    axios
      .get('/api/folders')
      .then(response => {
        setTreeData(convertToPrimeReactTree(response.data));
      })
      .catch(error => {
        toast.current.show({ severity: 'error', summary: 'Error', detail: 'Failed to fetch folder structure.' });
        console.error('Error fetching folders:', error);
      });
  };

  const fetchLatestDocuments = () => {
    axios
      .get('/api/download-document/latest/5')
      .then(response => {
        setLatestDocuments(response.data);
      })
      .catch(error => {
        toast.current.show({ severity: 'error', summary: 'Error', detail: 'Failed to fetch latest documents.' });
        console.error('Error fetching latest documents:', error);
      });
  };

  const convertToPrimeReactTree = data => {
    return data.map(item => ({
      key: item.path, // Use path as a unique key
      label: item.name,
      data: { path: item.path, folder: item.folder, id: item.id, filename: item.name },
      children: item.children ? convertToPrimeReactTree(item.children) : null,
      hasChildren: item.hasChildren,
    }));
  };

  const onUpload = event => {
    const files = event.files;

    let currentFolder = '';

    if (Object.keys(selectedNodeKeys).length > 0) {
      const selectedKey = selectedNodeKeys;
      // const selectedNode = treeData.find(node => node.key === selectedKey);

      currentFolder = selectedKey;
    }

    if (files && files.length > 0) {
      const formData = new FormData();
      formData.append('file', files[0]);
      formData.append('path', currentFolder);

      axios
        .post('/api/download-document/upload', formData)
        .then(response => {
          if (fileUploadRef.current) {
            fileUploadRef.current.clear();
          }
          toast.current.show({ severity: 'success', summary: 'Upload Successful', detail: response.data });
          fetchFolderStructure();
          fetchLatestDocuments();
        })
        .catch(error => {
          if (fileUploadRef.current) {
            fileUploadRef.current.clear();
          }
          toast.current.show({
            severity: 'error',
            summary: 'Upload Error',
            detail: error.response?.data?.detail || error.response?.data || 'Could not upload file.',
          });
          console.error('Error uploading file:', error);
        })
        .finally(() => {
          setUploadDialogVisible(false);
        });
    }
  };

  const onDownload = (id, filename) => {
    downloadDocument(id, filename);
  };

  const onDelete = id => {
    axios
      .delete(`/api/download-document/${id}`)
      .then(response => {
        toast.current.show({ severity: 'success', summary: 'Dokument úspešne vymazaný', detail: response.data });
        fetchFolderStructure();
        fetchLatestDocuments();
      })
      .catch(error => {
        if (fileUploadRef.current) {
          fileUploadRef.current.clear();
        }
        toast.current.show({ severity: 'error', summary: 'Chyba pri nahrávaní súboru', detail: error.response?.data?.detail });
        console.error('Error uploading file:', error);
      })
      .finally(() => {
        setDeleteDocumentDialog(false);
      });
  };

  const onDeleteFolder = path => {
    const formData = new FormData();
    formData.append('path', path);

    axios
      .delete(`/api/folders`, { data: { path } })
      .then(response => {
        toast.current.show({ severity: 'success', summary: 'Adresár úspešne vymazaný', detail: response.data });
        fetchFolderStructure();
        fetchLatestDocuments();
      })
      .catch(error => {
        if (fileUploadRef.current) {
          fileUploadRef.current.clear();
        }
        toast.current.show({ severity: 'error', summary: 'Chyba', detail: error.response?.data?.detail });
        console.error('Error uploading file:', error);
      })
      .finally(() => {
        setDeleteFolderDialog(false);
        setSelectedNodeKeys(null);
      });
  };

  const hideUploadDialog = () => {
    setUploadDialogVisible(false);
  };

  const showNewFolderDialog = () => {
    if (selectedNodeKeys && Object.keys(selectedNodeKeys).length > 0) {
      const selectedKey = selectedNodeKeys;
      // const selectedNode = treeData.find(node => node.key === selectedKey);

      if (selectedKey) {
        setNewFolderDialogVisible(true);
        setCurrentPath(selectedKey);
      } else {
        toast.current.show({ severity: 'warn', summary: 'Vyber adresára', detail: 'Vyberte adresára v strome.' });
      }
    } else {
      setNewFolderDialogVisible(true);
      setCurrentPath('');
    }
  };

  const hideNewFolderDialog = () => {
    setNewFolderDialogVisible(false);
    setNewFolderName('');
  };

  const handleCreateFolder = () => {
    if (newFolderName.trim()) {
      axios
        .post('/api/folders', { name: newFolderName, parentPath: currentPath })
        .then(response => {
          toast.current.show({ severity: 'success', summary: 'Priečinok vytvorený', detail: response.data });
          fetchFolderStructure();
        })
        .catch(error => {
          toast.current.show({
            severity: 'error',
            summary: 'Chyba',
            detail: error.response?.data?.detail || error.response?.data || 'Could not create folder.',
          });
          console.error('Error creating folder:', error);
        })
        .finally(() => {
          hideNewFolderDialog();
        });
    } else {
      toast.current.show({ severity: 'warn', summary: 'Prázdne meno', detail: 'Meno priečinku nemôže byť prázdne.' });
    }
  };

  const handleSearch = () => {
    if (searchTerm.trim()) {
      axios
        .get(`/api/folders/search?query=${searchTerm}`)
        .then(response => {
          setSearchResults(response.data);
          setTreeData(convertToPrimeReactTree(response.data));
          expandAll();
        })
        .catch(error => {
          toast.current.show({ severity: 'error', summary: 'Chyba', detail: error.response?.data?.detail });
          setSearchResults([]);
          setTreeData([]);
          console.error('Error searching:', error);
        });
    } else {
      fetchFolderStructure();
    }
  };

  const uploadDialogFooter = (
    <div>
      <Button label="Cancel" icon="pi pi-times" onClick={hideUploadDialog} className="p-button-text" />
    </div>
  );

  const newFolderDialogFooter = (
    <div>
      <Button label="Zrušiť" icon="pi pi-times" onClick={hideNewFolderDialog} className="p-button-text" />
      <Button
        className="p-button-success mr-1"
        style={{ backgroundColor: 'green' }}
        label="Vytvoriť"
        icon="pi pi-check"
        onClick={handleCreateFolder}
      />
    </div>
  );

  const nodeTemplate = (node, options) => {
    console.log('node', node);
    return (
      <div className="flex align-items-center">
        <i className={!node.data.folder ? 'pi pi-file' : 'pi pi-folder'} style={{ fontSize: '1rem', marginRight: '5px' }}></i>
        <span style={{ fontWeight: node.data.folder ? 'bold' : 'normal' }}>{node.label}</span>
        {!node.data.folder && (
          <div className="ml-2">
            <Button
              style={{ width: '1rem', height: '2rem', backgroundColor: 'gray', borderColor: 'gray' }}
              icon="pi pi-download"
              size="small"
              className="p-button-rounded p-button-success mr-1"
              tooltip="Stiahnuť"
              onClick={() => onDownload(node.data.id, node.data.filename)}
            />
            {isAdminOrAssistant && (
              <Button
                style={{ marginLeft: '10px', width: '1rem', height: '2rem' }}
                icon="pi pi-trash"
                size="small"
                className="p-button-rounded p-button-danger"
                tooltip="Odstrániť dokument"
                onClick={() => {
                  setSeletedDeleteDocument(node.data.id);
                  setDeleteDocumentDialog(true);
                }}
              />
            )}
          </div>
        )}
        {isAdminOrAssistant && node.data.folder && !node.hasChildren /*(!node.children || node.children.length === 0)*/ && (
          <div className="ml-2">
            <Button
              style={{ marginLeft: '10px', width: '1rem', height: '2rem' }}
              icon="pi pi-trash"
              size="small"
              className="p-button-rounded p-button-danger"
              tooltip="Odstrániť priečinok"
              onClick={() => {
                setSeletedDeleteFolder(node.data.path);
                setDeleteFolderDialog(true);
              }}
            />
          </div>
        )}
      </div>
    );
  };

  const deleteDocumentDialogFooter = () => (
    <React.Fragment>
      <Button label="Nie" icon="pi pi-times" outlined onClick={() => setDeleteDocumentDialog(false)} />
      <Button label="Áno" icon="pi pi-check" severity="danger" onClick={() => onDelete(seletedDeleteDocument)} />
    </React.Fragment>
  );

  const deleteFolderDialogFooter = () => (
    <React.Fragment>
      <Button label="Nie" icon="pi pi-times" outlined onClick={() => setDeleteFolderDialog(false)} />
      <Button label="Áno" icon="pi pi-check" severity="danger" onClick={() => onDeleteFolder(seletedDeleteFolder)} />
    </React.Fragment>
  );

  useEffect(() => {
    const delayDebounce = setTimeout(() => {
      handleSearch();
    }, 500); // 500ms delay after typing stops

    return () => clearTimeout(delayDebounce); // cleanup on new keystroke
  }, [searchTerm]);

  const handleKeyDown = event => {
    if (event.key === 'Enter') {
      handleSearch();
    }
  };

  const expandAll = () => {
    const _expanded = {};
    const walk = node => {
      if (node.children?.length) {
        _expanded[node.key] = true; // open this node
        node.children.forEach(walk); // recurse
      }
    };
    treeData.forEach(walk);
    setExpandedKeys(_expanded);
  };

  const collapseAll = () => setExpandedKeys({});

  // Optional helper if you ever want “open everything to level N”
  const expandToLevel = (level = 1) => {
    const _expanded = {};
    const walk = (node, depth) => {
      if (depth < level && node.children?.length) {
        _expanded[node.key] = true;
        node.children.forEach(c => walk(c, depth + 1));
      }
    };
    treeData.forEach(n => walk(n, 0));
    setExpandedKeys(_expanded);
  };

  return (
    <div className="container">
      <Toast ref={toast} />
      <h1>Dokumenty na stiahnutie</h1>

      <div className="p-inputgroup mb-2">
        <InputText
          placeholder="Hľadať dokumenty alebo priečinky"
          onKeyDown={handleKeyDown}
          value={searchTerm}
          onChange={e => setSearchTerm(e.target.value)}
        />
        <Button icon="pi pi-search" onClick={handleSearch} />
      </div>

      <div className="grid">
        <div className="col-12">
          <div className="card">
            {isAdminOrAssistant && (
              <div className="flex mb-2">
                <Button
                  style={{ backgroundColor: 'green', borderColor: 'green' }}
                  label="Nový priečinok"
                  icon="pi pi-folder"
                  onClick={showNewFolderDialog}
                  className="mr-2"
                />
                <FileUpload
                  disabled={!selectedNodeKeys}
                  ref={fileUploadRef}
                  mode="basic"
                  multiple={false}
                  auto={true}
                  name="files"
                  accept=".csv,.xml,.pdf,.xlsx,.png,.jpg,.jpeg,.doc,.docx,.xls,.ppt,.pptx,.txt,.odt,.ods,.odp,.ics,.zip,.tiff"
                  customUpload
                  uploadHandler={onUpload}
                  chooseLabel="Nahrať dokument"
                  uploadLabel="Nahrať"
                  cancelLabel="Zrušiť"
                />
              </div>
            )}
            <Tree
              value={treeData}
              nodeTemplate={nodeTemplate}
              selectionMode="single"
              selectionKeys={selectedNodeKeys}
              onSelectionChange={e => setSelectedNodeKeys(e.value)}
              expandedKeys={expandedKeys}
              onToggle={e => setExpandedKeys(e.value)}
            />
          </div>
        </div>
      </div>

      <Dialog
        header="Nahrať dokument"
        visible={uploadDialogVisible}
        style={{ width: '50vw' }}
        modal
        onHide={hideUploadDialog}
        footer={uploadDialogFooter}
      >
        <FileUpload mode="basic" name="file" url="/api/download-document/upload" onUpload={onUpload} accept="*" maxFileSize={1000000} />
      </Dialog>

      <Dialog
        header="Nový priečinok"
        visible={newFolderDialogVisible}
        style={{ width: '30vw' }}
        modal
        onHide={hideNewFolderDialog}
        footer={newFolderDialogFooter}
      >
        <div className="p-field mt-2">
          <label htmlFor="newFolderName" className="form-label">
            Názov priečinku:
          </label>
          <InputText id="newFolderName" value={newFolderName} onChange={e => setNewFolderName(e.target.value)} />
        </div>
      </Dialog>

      <Dialog
        visible={deleteDocumentDialog}
        style={{ width: '32rem' }}
        breakpoints={{ '960px': '75vw', '641px': '90vw' }}
        header="Potvrdenie"
        modal
        footer={deleteDocumentDialogFooter}
        onHide={() => setDeleteDocumentDialog(false)}
      >
        <div className="confirmation-content">
          <i className="pi pi-exclamation-triangle mr-3" style={{ fontSize: '2rem' }} />
          <span>Naozaj chcete vymazať tento dokument ?</span>
        </div>
      </Dialog>

      <Dialog
        visible={deleteFolderDialog}
        style={{ width: '32rem' }}
        breakpoints={{ '960px': '75vw', '641px': '90vw' }}
        header="Potvrdenie"
        modal
        footer={deleteFolderDialogFooter}
        onHide={() => setDeleteFolderDialog(false)}
      >
        <div className="confirmation-content">
          <i className="pi pi-exclamation-triangle mr-3" style={{ fontSize: '2rem' }} />
          <span>Naozaj chcete vymazať tento priečinok ?</span>
        </div>
      </Dialog>
    </div>
  );
};

export default DownloadDocument;
